import { useState, useEffect, useMemo, useCallback } from 'react';
import {
    CCard,
    CCardHeader,
    CCardBody,
    CButton,
} from '@coreui/react';
import { CSmartTable } from '@coreui/react-pro';

import { COMPONENT_NAME, DEFAULT_DATE_TIME_FORMAT } from '../../../../../../constants';
import { formatDateTimeZone, toastError } from '../../../../../../utils';
import { useDebounce } from '../../../../../../helpers/customHooks'
import { useDispatch } from 'react-redux';
import { setFlexibleModal } from '../../../../../../actions/admin';

const CompanyIdentifiersPanel = ({ account, companyId, title, hiddenCard = false }) => {
    const { id: accountId, timeZone } = account;
    const dispatch = useDispatch();
    const [items, setItems] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [rawColumnFilter, setRawColumnFilter] = useState({});
    const debouncedColumnFilter = useDebounce(rawColumnFilter, 500);

    const [activePage, setActivePage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);

    const columns = useMemo(() => [
        { key: 'value', label: 'Value', _props: { scope: 'col' } },
        { key: 'type', label: 'Type', _props: { scope: 'col' } },
        { key: 'source', label: 'Source', _props: { scope: 'col' }, sorter: false },
        { key: 'sourcePath', label: 'Source Path', _props: { scope: 'col' }, sorter: false },
        { key: 'validStatus', label: 'Valid Status', _props: { scope: 'col' }, sorter: false },
        { key: 'validAt', label: 'Valid At', _props: { scope: 'col' }, sorter: false },
        { key: 'enrichStatus', label: 'Enrich Status', _props: { scope: 'col' }, sorter: false },
        { key: 'enrichAt', label: 'Enrich At', _props: { scope: 'col' }, sorter: false },
        { key: 'companyStatus', label: 'Company Status', _props: { scope: 'col' } },
        {
            key: 'actions',
            label: 'Actions',
            _props: { scope: 'col', className: 'text-center' },
            filter: false,
            sorter: false,
        }
    ], []);

    const fetchPersons = useCallback(async (currentPage, currentPageSize, currentColumnFilter) => {
        if (!accountId) {
            setItems([]);
            setTotalPages(0);
            setError("Account ID is required to fetch persons.");
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            setItems([]);
            setTotalPages(0);
        } catch (err) {
            console.error("API call failed:", err);
            const errorMessage = err.message || 'An unexpected error occurred.';
            toastError({ data: { message: errorMessage } });
            setError(errorMessage);
            setItems([]);
            setTotalPages(0);
        } finally {
            setIsLoading(false);
        }
    }, [accountId, companyId]); // eslint-disable-line

    useEffect(() => {
        fetchPersons(activePage, pageSize, debouncedColumnFilter);
    }, [fetchPersons, activePage, pageSize, debouncedColumnFilter]);

    const handleRetrieve = useCallback((id) => {
        dispatch(
            setFlexibleModal({
                show: true,
                component: COMPONENT_NAME.ADMIN_COMPANY_IDENTIFIER,
                attributes: {
                    id,
                }
            })
        );
    }, [dispatch]); // eslint-disable-line

    const scopedColumns = useMemo(() => ({
        validAt: (item) => (
            <td className='text-nowrap'>{item.validAt ? formatDateTimeZone(item.validAt, DEFAULT_DATE_TIME_FORMAT, timeZone) : ''}</td>
        ),
        enrichAt: (item) => (
            <td className='text-nowrap'>{item.enrichAt ? formatDateTimeZone(item.enrichAt, DEFAULT_DATE_TIME_FORMAT, timeZone) : ''}</td>
        ),
        actions: (item) => (
            <td className="text-center">
                <CButton
                    color="info"
                    variant="outline"
                    size="sm"
                    onClick={() => handleRetrieve(item.id)}
                >
                    View
                </CButton>
            </td>
        ),
    }), [handleRetrieve]); // eslint-disable-line

    const handlePageChange = (newPage) => {
        setActivePage(newPage);
    };

    const handleItemsPerPageChange = (newPageSize) => {
        setPageSize(newPageSize);
        setActivePage(1);
    };

    const handleColumnFilterChange = (newFilter) => {
        setRawColumnFilter(newFilter);
        setActivePage(1);
    }

    const tableComponent = (
        <>
            {error && <div className="alert alert-danger">{error}</div>}
            <CSmartTable
                loading={isLoading}
                noItemsLabel={!isLoading && items.length === 0 ? "No data found for this account." : "Loading data..."}
                items={items}
                columns={columns}
                scopedColumns={scopedColumns}
                pagination={{
                    external: true,
                }}
                paginationProps={{
                    activePage: activePage,
                    pages: totalPages,
                }}
                onActivePageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                onColumnFilterChange={handleColumnFilterChange}
                itemsPerPage={pageSize}
                itemsPerPageSelect
                columnFilter={{ external: true }}
                columnSorter
                tableProps={{
                    hover: true,
                    responsive: true,
                    className: 'table-striped align-middle'
                }}
            />
        </>
    );

    return (
        <>
            {hiddenCard ? (
                tableComponent
            ) : (
                <CCard>
                    {title && (
                        <CCardHeader>
                            <h5 className='mb-0'>{title}</h5>
                        </CCardHeader>
                    )}
                    <CCardBody>
                        {tableComponent}
                    </CCardBody>
                </CCard>
            )}
        </>
    );
};

export default CompanyIdentifiersPanel;